import { observer } from "mobx-react-lite";
import { useContext } from "react";

import {
  AnimatedListWithKeys,
  Heading,
  IconButton,
  NoDataTile,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import { AppointmentVisitDto } from "@libs/gateways/acc/AccGateway.dtos.ts";
import { ClaimAdjustmentFormLabels } from "@modules/acc/screens/claim-adjustment/components/ClaimAdjustmentEnums.ts";
import {
  ClaimManagementCardIds,
  ClaimSectionHeadings
} from "@modules/acc/screens/claim/components/ClaimFormEnums.ts";
import { ClaimCard } from "@modules/acc/screens/shared-components/ClaimCard.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { useCardFormLayoutContext } from "@ui-components/card-form-layout/context/CardFormLayoutHelperContext.ts";
import { useFieldArray } from "@ui-components/form/submission-form/hooks/useFieldArray.ts";

import { claimManagementFormNameOf } from "../../claim/claim.utils.ts";
import { ClaimContext } from "../../claim/context/ClaimContext.ts";
import { VisitsFormFields } from "./VisitsFormFields.tsx";

const getAnimationKey = (dto: AppointmentVisitDto): string =>
  `${dto.contractType} ${dto.providerType}`;

export const VisitsFormSection: React.FC = observer(() => {
  const { booking } = useStores();
  const { claim } = useContext(ClaimContext);
  const { selectedMenuItemId } = useCardFormLayoutContext();

  const { fields } = useFieldArray<AppointmentVisitDto>(
    claimManagementFormNameOf("appointmentVisits")
  );

  const items = fields.value?.filter(x => x.contractType) ?? [];

  return (
    <ClaimCard
      id={ClaimManagementCardIds.visits}
      openOnRender={selectedMenuItemId === ClaimManagementCardIds.visits}
      heading={
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          grow
          styles={{ root: { paddingRight: 4 } }}
        >
          <Heading variant="modal-heading">
            {ClaimSectionHeadings.consults}
          </Heading>
          <TooltipHost
            content={
              items.length
                ? "View appointment information"
                : "No linked appointments"
            }
          >
            <IconButton
              disabled={items.length === 0}
              iconProps={{ iconName: "calendar" }}
              onClick={evt => {
                evt.stopPropagation();
                booking.ui.setAppointmentInformationModalSettings({
                  claimId: claim.id,
                  contactId: claim.patientId ?? ""
                });
              }}
            />
          </TooltipHost>
        </Stack>
      }
      iconName="SearchCalendar"
      styles={{ heading: { flex: 1 } }}
    >
      {items.length === 0 && (
        <NoDataTile
          textProps={{
            text: ClaimAdjustmentFormLabels.noConsults
          }}
          showBoxShadow={false}
          linkProps={{ hidden: true }}
        />
      )}

      {items.length > 0 && (
        <Stack tokens={{ childrenGap: 16 }}>
          <AnimatedListWithKeys
            items={items.map(x => ({ ...x, id: getAnimationKey(x) }))}
            onItemIdRemoved={(key: string) => {
              const index = fields.value.findIndex(
                v => getAnimationKey(v) === key
              );
              fields.remove(index);
            }}
          >
            {(key, onRemoveItem) => {
              const index = fields.value.findIndex(
                p => getAnimationKey(p) === key
              );

              if (index >= 0) {
                return (
                  <VisitsFormFields
                    name={`${fields.name}[${index}]`}
                    index={index}
                    onRemoveItem={onRemoveItem}
                  />
                );
              }
              return null;
            }}
          </AnimatedListWithKeys>
        </Stack>
      )}
    </ClaimCard>
  );
});
